import React, { useState, useEffect } from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import LineTabs from '~/components/molecule/LineTabs';
import SearchInput from '~/components/molecule/SearchInput';
import CardsList from './components/CardsList';
import {
  GetLfaConversionFilterType,
  useGetLfaConversionsQuery,
  type GetLfaConversionsTotalsQuery,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import ErrorScreen from '~/components/page/ErrorScreen';
import Loading from '~/components/atom/Loading';
import parseUrlParams from '../../utils/parseUrlParams';
import getConversionTabs from '../../utils/getConversionTabs';
import styled from 'styled-components';
import { MAX_HEIGHT } from '../../constants';

const CARDS_OVERVIEW_WIDTH = '620px';
const text = {
  searchLabel: 'Zoeken...',
};

type Props = {
  totals: GetLfaConversionsTotalsQuery;
  selectedConversionId: string | null;
  selectedTab: GetLfaConversionFilterType;
  setSelectedTab: (tab: GetLfaConversionFilterType) => void;
  setSelectedConversionId: (id: string | null) => void;
  refetch: () => void;
};

const CardsOverview: React.FCC<Props> = ({
  totals,
  selectedConversionId,
  setSelectedConversionId,
  selectedTab,
  setSelectedTab,
  refetch,
}) => {
  const { id: accountId } = useCurrentAccount();
  const [searchQuery, setSearchQuery] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize from URL parameters on component mount only
  useEffect(() => {
    if (!isInitialized) {
      const { queryStr, tab } = parseUrlParams(
        new URLSearchParams(window.location.search),
      );
      if (queryStr) {
        setSearchQuery(queryStr);
      }
      if (tab) {
        setSelectedTab(tab);
      }

      setIsInitialized(true);
    }
  }, [isInitialized, setSelectedTab, setSelectedConversionId]);

  const { data, loading, error } = useGetLfaConversionsQuery({
    variables: {
      filter: GetLfaConversionFilterType.All,
      accountId: accountId,
    },
  });

  if (loading) return <Loading />;
  if (error || !data || !data.getLfaConversions) return <ErrorScreen />;

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleTabSelect = (key: string) => {
    setSearchQuery('');
    refetch();
    setSelectedConversionId(null);
    setSelectedTab(key as GetLfaConversionFilterType);
  };

  return (
    <Container
      direction="column"
      align="center"
      width={CARDS_OVERVIEW_WIDTH}
      backgroundColor={{ group: 'white' }}
      border={{
        radius: 'm',
        width: 's',
        color: { group: 'primary', variant: 'translucent' },
      }}
      padding={['l']}
      gap="xs"
      height={MAX_HEIGHT}
    >
      <JustificationContainer
        direction="column"
        padding={[null, 'xxs']}
        width="100%"
      >
        <LineTabs
          justification="space-around"
          tabs={getConversionTabs(totals)}
          onSelect={handleTabSelect}
          selectedKey={selectedTab}
        />

        <SearchInput
          width="100%"
          placeholder={text.searchLabel}
          value={searchQuery}
          onInputChange={handleSearchChange}
          dataTestId="search-input"
        />
      </JustificationContainer>
      <CardsList
        filter={selectedTab}
        selectedId={selectedConversionId}
        onChange={id => setSelectedConversionId(id)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
    </Container>
  );
};

const Container = styled(JustificationContainer)`
  flex-shrink: 0;
  overflow: auto;
  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export default CardsOverview;
